// ============================================================
// Монеты и призы рулетки · Coins & Prizes API
// Все запросы с JWT: Authorization: Bearer <jwt>
// ============================================================
const API_BASE = 'https://baaack.zabanishumresh.com';

export const SPIN_COST_DEFAULT = 10;   // SPIN_COST_COINS
export const DAILY_COINS = 5;          // DAILY_CHECKIN_COINS
export const PURCHASE_COINS = 10;      // PURCHASE_COINS
export const REFERRAL_COINS = 20;      // REFERRAL_PURCHASE_COINS
export const CHECKIN_DAY_KEY = 'coins_checkin_day';
const COINS_CACHE_KEY = 'coins';

const authHeader = () => {
  const t = sessionStorage.getItem('jwtToken') || '';
  return t.startsWith('Bearer ') ? t : `Bearer ${t}`;
};

export class ApiError extends Error {
  constructor(status, body) {
    super(`HTTP ${status}`);
    this.status = status;
    this.body = body;
  }
}

async function request(path, { method = 'GET', body } = {}) {
  const headers = { accept: 'application/json', Authorization: authHeader() };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined
  });
  let data = null;
  try { data = await res.json(); } catch (e) { /* пустой ответ */ }
  if (!res.ok) throw new ApiError(res.status, data);
  return data;
}

// 1. GET /coins/balance → { coins }
export const getCoinsBalance = () =>
  request('/coins/balance').then((d) => {
    const v = Number(d?.coins) || 0;
    setCachedCoins(v);
    return v;
  });

// 2. GET /coins/history?limit&offset → { items: [{ amount, reason, created_at }] }
export const getCoinsHistory = (limit = 20, offset = 0) =>
  request(`/coins/history?limit=${limit}&offset=${offset}`).then((d) => d?.items || []);

// 3. GET /coins/spin-prizes → { spin_cost, prizes: [...] }
export const getSpinPrizes = () => request('/coins/spin-prizes');

// 4. POST /coins/spin → { prize_id, prize_db_id, name, rarity, prize_type, prize_data, coins_left }
export const spinRoulette = () => request('/coins/spin', { method: 'POST', body: {} });

// 5. GET /coins/prizes?include_used → { prizes: [...] }
export const getMyPrizes = (includeUsed = false) =>
  request(`/coins/prizes?include_used=${includeUsed}`).then((d) => d?.prizes || []);

// 6. POST /coins/prizes/{id}/apply  (tariff: {}, discount: { tariff_name, months })
export const applyPrize = (prizeId, body = {}) =>
  request(`/coins/prizes/${encodeURIComponent(prizeId)}/apply`, { method: 'POST', body });

// 7. POST /coins/checkin → { coins_earned, coins_total, already_done }
export const dailyCheckin = () => request('/coins/checkin', { method: 'POST', body: {} });

// ===== Кэш монет =====
export const getCachedCoins = () => {
  try {
    const v = sessionStorage.getItem(COINS_CACHE_KEY);
    return v === null ? null : Number(v);
  } catch (e) { return null; }
};
export const setCachedCoins = (v) => {
  try { sessionStorage.setItem(COINS_CACHE_KEY, String(v)); } catch (e) { /* noop */ }
};

// ===== Даты: чекин считается по московскому времени =====
export const moscowDay = (input) => {
  const d = input === undefined ? new Date() : new Date(input);
  return d.toLocaleDateString('en-CA', { timeZone: 'Europe/Moscow' }); // YYYY-MM-DD
};

// ===== Склонения =====
export function pluralRu(n, one, few, many) {
  const a = Math.abs(n) % 100, b = a % 10;
  if (a > 10 && a < 20) return many;
  if (b === 1) return one;
  if (b >= 2 && b <= 4) return few;
  return many;
}
export const monthWord = (n) => pluralRu(n, 'месяц', 'месяца', 'месяцев');
export const coinWord = (n) => pluralRu(n, 'монета', 'монеты', 'монет');
export const dayWord = (n) => pluralRu(n, 'день', 'дня', 'дней');

// ===== Нормализация призов для иконок и барабана =====
export const RARITY_LEVEL = { 'Обычный': 0, 'Редкий': 1, 'Эпический': 2, 'Легендарный': 3 };
const DEVICES = { Basic: 1, Medium: 3, Premium: 10 };
const cap = (s) => {
  const t = String(s || '').trim().toLowerCase();
  return t ? t[0].toUpperCase() + t.slice(1) : '';
};

/*
  kind (для PrizeIcon): 'tariff' | 'jackpot' | 'disc' | 'all' | 'coins' | 'money'
  type (с бэка):        'tariff' | 'discount' | 'coins' | 'balance'
*/
const parseData = (raw) => {
  if (!raw) return {};
  if (typeof raw === 'string') { try { return JSON.parse(raw) || {}; } catch (e) { return {}; } }
  return raw;
};

export function normalizePrize(p) {
  if (!p) return null;
  const d = parseData(p.prize_data);
  const level = RARITY_LEVEL[p.rarity] ?? 0;
  const base = {
    id: p.prize_id ?? p.id,
    type: p.prize_type,
    rarity: p.rarity || 'Обычный',
    level,
    probability: p.probability ?? null
  };

  if (p.prize_type === 'tariff') {
    const plan = cap(d.plan) || 'Basic';
    const months = Number(d.months) || 1;
    const dev = DEVICES[plan] ?? 1;
    return {
      ...base,
      kind: level === 3 ? 'jackpot' : 'tariff',
      plan, months,
      title: p.name || `${plan} на ${months} ${monthWord(months)}`,
      sub: `${dev} ${pluralRu(dev, 'устройство', 'устройства', 'устройств')}`
    };
  }

  if (p.prize_type === 'discount') {
    const t = String(d.target || 'any').toLowerCase();
    const target = t === 'any' ? 'any' : cap(t);
    const fromName = String(p.name || '').match(/(\d+(?:[.,]\d+)?)\s*%/);
    const percent = Number(d.percent ?? d.discount_percent ?? d.discount ?? d.value)
      || (fromName ? Number(fromName[1].replace(',', '.')) : 0);
    const minMonths = Number(d.min_months) || 1;
    return {
      ...base,
      kind: target === 'any' ? 'all' : 'disc',
      percent, target, minMonths,
      title: p.name || `−${percent}% на ${target === 'any' ? 'любой тариф' : target}`,
      sub: minMonths > 1 ? `При оплате от ${minMonths} ${monthWord(minMonths)}` : 'На любой срок'
    };
  }

  if (p.prize_type === 'coins') {
    const amount = Number(d.amount ?? d.coins) || 0;
    return { ...base, kind: 'coins', amount, title: p.name || `${amount} ${coinWord(amount)}`, sub: 'Сразу на счёт монет' };
  }

  if (p.prize_type === 'balance') {
    const amount = Number(d.amount ?? d.rub ?? d.balance) || 0;
    return { ...base, kind: 'money', amount, title: p.name || `${amount} ₽ на баланс`, sub: 'Сразу на баланс' };
  }

  return { ...base, kind: 'coins', amount: 0, title: p.name || 'Приз', sub: '' };
}

// ===== Купоны на скидку =====
export const SELECTED_COUPON_KEY = 'selected_coupon_id';

// Активные купоны: prize_type == 'discount' && status == 'active'
export const getActiveCoupons = () =>
  getMyPrizes(false).then((list) =>
    list.map(normalizeOwned).filter((c) => c && c.type === 'discount' && c.status === 'active'
      && (!c.expiresAt || c.expiresAt > Date.now()))
  );

// Подходит ли купон к тарифу/сроку. Возвращает null или текст причины.
export const couponBlockReason = (c, tariffName, months) => {
  if (!c) return null;
  if (c.target !== 'any' && !String(tariffName || '').toLowerCase().includes(c.target.toLowerCase())) {
    return `Только для ${c.target}`;
  }
  if (months < c.minMonths) return `От ${c.minMonths} ${monthWord(c.minMonths)}`;
  return null;
};

// round(базовая × (1 − percent / 100), 2)
export const applyCouponPrice = (price, percent) =>
  Math.round(price * (1 - percent / 100) * 100) / 100;

// Одинаковые купоны (процент + тариф + мин. срок) → одна строка с count; берём тот, что сгорит раньше
export const groupCoupons = (list) => {
  const map = new Map();
  list.forEach((c) => {
    const k = `${c.percent}|${c.target}|${c.minMonths}`;
    const g = map.get(k);
    if (!g) { map.set(k, { ...c, count: 1, ids: [c.dbId] }); return; }
    const count = g.count + 1;
    const ids = [...g.ids, c.dbId];
    const earlier = c.expiresAt && (!g.expiresAt || c.expiresAt < g.expiresAt);
    map.set(k, earlier ? { ...c, count, ids } : { ...g, count, ids });
  });
  return [...map.values()];
};

export const setSelectedCoupon = (id) => {
  try { id ? sessionStorage.setItem(SELECTED_COUPON_KEY, id) : sessionStorage.removeItem(SELECTED_COUPON_KEY); } catch (e) { /* noop */ }
};
export const getSelectedCoupon = () => {
  try { return sessionStorage.getItem(SELECTED_COUPON_KEY); } catch (e) { return null; }
};

// Приз из копилки (GET /coins/prizes)
export function normalizeOwned(p) {
  const n = normalizePrize(p);
  if (!n) return null;
  return {
    ...n,
    dbId: p.id,
    status: p.status || 'active',
    expiresAt: p.expires_at ? new Date(p.expires_at) : null,
    wonAt: p.won_at ? new Date(p.won_at) : null
  };
}
